import React, { useState } from 'react';
import { Clipboard } from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { getBaseUrl } from '@/utils/url';

const INVITE_LINK = getBaseUrl();

// Helper to get the correct Edge Function URL for local/production
const getInviteFunctionUrl = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:54321/functions/v1/invite_friend';
  }
  if (supabaseUrl) {
    return `${supabaseUrl}/functions/v1/invite_friend`;
  }
  return '/functions/v1/invite_friend';
};

export default function InviteFriend() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const validateEmail = (value: string) => {
    if (!value) return '';
    return /^[^@]+@[^@]+\.[^@]+$/.test(value) ? '' : 'Invalid email';
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(INVITE_LINK);
      setCopied(true);
      toast({ title: 'Link copied successfully!', variant: 'default' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      toast({ title: 'Failed to copy link. Please try again.', variant: 'destructive' });
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(validateEmail(e.target.value));
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(validateEmail(email));
    if (validateEmail(email)) return;
    setLoading(true);
    
    try {
      console.log('📧 Sending invite to:', email);
      
      // Call the Edge Function via supabase client to ensure authentication
      const { data, error } = await supabase.functions.invoke('invite_friend', {
        body: { email },
      });
      
      console.log('📬 Response data:', data);
      console.log('❌ Response error:', error);
      
      if (error) {
        console.error('❌ Full error object:', error);
        const backendMsg =
          (typeof error.error === 'string' ? error.error : error.error?.message) ||
          error.message ||
          'Failed to send invitation.';
        toast({ title: backendMsg, variant: 'destructive' });
        setLoading(false);
        return;
      }
      
      toast({ title: `Convite enviado com sucesso para ${email}!`, variant: 'default' });
      setEmail('');
    } catch (err) {
      console.error('❌ Catch error:', err);
      toast({ title: 'Falha ao enviar convite. Tente novamente.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#18181b] flex flex-col">
      <TopNavigation />
      <main className="flex-1 flex flex-col items-center justify-center pb-24 pt-20">
        <div className="w-full max-w-md px-6 py-8" style={{ background: 'transparent', borderRadius: 24 }}>
          <form onSubmit={handleInvite} className="w-full space-y-8">
            <h1 className="text-2xl font-bold font-sans tracking-tight text-white mb-8 text-left">Invite a Friend</h1>
            {/* Copy Link Section */}
            <div className="space-y-2 mb-8">
              <p className="text-gray-300">Share this link with your friends to invite them to sOSSiety!</p>
              <div className="flex items-stretch w-full gap-2">
                <input
                  type="text"
                  value={INVITE_LINK}
                  readOnly
                  className="font-medium flex-1 h-12 w-full box-border rounded-md border border-white bg-white/5 px-3 py-2 text-base text-white placeholder:text-white/60 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
                <button
                  onClick={handleCopy}
                  type="button"
                  className="p-2 h-12 bg-sossiety-red-600 text-white rounded-lg hover:bg-sossiety-red-700 transition flex items-center justify-center"
                  aria-label="Copy link"
                  style={{ minWidth: 48 }}
                >
                  <Clipboard className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-[#232326]" />
              <span className="mx-4 text-gray-400 font-semibold">OR</span>
              <div className="flex-grow border-t border-[#232326]" />
            </div>
            {/* Invite by Email Section */}
            <div className="space-y-4">
              <label className="block mb-1 font-medium text-gray-200">Or, invite them by email:</label>
              <Input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="friend@email.com"
                error={!!emailError}
                disabled={loading}
              />
              {emailError && <div className="text-red-400 text-sm">{emailError}</div>}
              <button
                type="submit"
                disabled={!!emailError || !email || loading}
                className="w-full px-4 py-4 bg-sossiety-red-600 text-white rounded-full font-semibold hover:bg-sossiety-red-700 transition disabled:opacity-50 mt-2"
                style={{ borderRadius: '9999px' }}
              >
                {loading ? 'Sending...' : 'Invite'}
              </button>
            </div>
          </form>
        </div>
      </main>
      <BottomNavigation />
    </div>
  );
} 